
import React, { useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { Form, Input, Select, Upload } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import axios from "axios";
import { toast } from "react-toastify";
import { switchEndpoint } from "../../../../../../redux/actions/endpoints";
import { storage } from "../../../../../../scopedStorage";
import "./index.css";

const { Option } = Select;

const TOAST_CONFIG = {
  position: "top-right",
  autoClose: 5000,
  hideProgressBar: false,
  pauseOnHover: true,
  progress: undefined,
  theme: "light",
};

const DriverModal = ({ showDrop, handleCloseDrop, modalInfo, setModalInfo }) => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const [previewUrl, setPreviewUrl] = useState(null);
  const [driverImageFile, setDriverImageFile] = useState(null);

  const token = useMemo(() => storage.getItem("token"), []);

  // Treat "imageSrc" as "new image chosen in this session"
  const imageSrc = previewUrl; // <-- this is the new image preview (if user selected a file)

  useEffect(() => {
    if (showDrop && modalInfo) {
      form.setFieldsValue({
        driverName: modalInfo?.name || "",
        driverPhone: modalInfo?.phone || "",
        vehiclePlate: modalInfo?.vehicle_number || "",
        vehicleType: modalInfo?.vehicle_type || "",
      });
      return;
    }

    if (!showDrop) {
      form.resetFields();
      setDriverImageFile(null);
      setPreviewUrl(null);
    }
  }, [showDrop, modalInfo, form]);

  // Revoke the object URL to avoid memory leaks
  useEffect(() => {
    if (!previewUrl) return;
    return () => URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const handleImageBeforeUpload = (file) => {
    setDriverImageFile(file);

    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);

    return false; // prevent AntD auto-upload
  };

  const updateDriverWithoutImage = async (values) => {
    const payload = {
      name: values.driverName,
      phone: values.driverPhone,
      vehicle_number: values.vehiclePlate,
      vehicle_type: values.vehicleType,
      // No image field sent (or set to "" if your API requires it)
      // image_url: "",
    };

    return axios.patch(
      `${switchEndpoint}/factory/update-driver/${modalInfo?.id}`,
      payload,
      { headers: { Authorization: `Bearer ${token}` } }
    );
  };

  const updateDriverWithImage = async (values) => {
    if (!driverImageFile) {
      toast.error("Please select an image first", TOAST_CONFIG);
      throw new Error("No image file selected");
    }

    // 1) upload image
    const formData = new FormData();
    formData.append("file", driverImageFile);

    const uploadRes = await axios.post(
      `${switchEndpoint}/upload-image`,
      formData,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const uploadedUrl = uploadRes?.data?.image_url;

    // 2) update driver with image_url
    const payload = {
      name: values.driverName,
      phone: values.driverPhone,
      vehicle_number: values.vehiclePlate,
      vehicle_type: values.vehicleType,
      image_url: uploadedUrl || "",
    };

    return axios.patch(
      `${switchEndpoint}/factory/update-driver/${modalInfo?.id}`,
      payload,
      { headers: { Authorization: `Bearer ${token}` } }
    );
  };

  const onFinish = async (values) => {
    setLoading(true);

    try {
      const res = imageSrc
        ? await updateDriverWithImage(values) // if imageSrc exists -> call withImage flow
        : await updateDriverWithoutImage(values); // otherwise -> withoutImage flow

      toast.success(res?.data?.message || "Driver updated successfully!", TOAST_CONFIG);

      handleCloseDrop();
      window.location.reload();
      setModalInfo({});
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to update driver",
        TOAST_CONFIG
      );
    } finally {
      setLoading(false);
    }
  };

  const onClose = () => {
    handleCloseDrop();
    setModalInfo({});
  };

  const digitsOnlyMax11 = (value = "") => String(value).replace(/\D/g, "").slice(0, 11);

  return (
    <Modal
      show={showDrop}
      onHide={() => {
        onClose();
      }}
      backdrop="static"
      keyboard={false}
      size="lg"
    >
      <Modal.Header closeButton />
      <Modal.Body>
        <div className="mx-2">
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            className="driver-form"
          >
            <div className="driver-form-grid">
              <Form.Item
                label="Driver Name"
                name="driverName"
                rules={[{ required: true, message: "Please enter driver name" }]}
              >
                <Input className="inputStyle" placeholder="Enter driver name" />
              </Form.Item>

              <Form.Item
  label="Driver Phone Number"
  name="driverPhone"
  normalize={digitsOnlyMax11}
  rules={[
    { required: true, message: "Please enter driver phone number" },
    { len: 11, message: "Phone number must be exactly 11 digits" }, // blocks submit if < 11 or > 11
    { pattern: /^\d+$/, message: "Only numbers are allowed" },
  ]}
>
  <Input
    inputMode="numeric"
    className="inputStyle"
    placeholder="Enter driver phone number"
    maxLength={11}
    autoComplete="tel"
  />
</Form.Item>
            </div>

            <div className="driver-form-grid">
              <Form.Item
                label="Vehicle Plate No"
                name="vehiclePlate"
                rules={[
                  { required: true, message: "Please enter vehicle plate no" },
                ]}
              >
                <Input className="inputStyle" placeholder="Enter vehicle plate no" />
              </Form.Item>

              <Form.Item
                label="Vehicle Type"
                name="vehicleType"
                rules={[
                  { required: true, message: "Please select vehicle type" },
                ]}
              >
                <Select placeholder="Select vehicle type">
                  <Option value="tricycle">Tricycle</Option>
                  <Option value="truck">Truck</Option>
                  {/* <Option value="bike">Bike</Option> */}
                </Select>
              </Form.Item>
            </div>

            <Form.Item label="">
              <Upload
                accept="image/*"
                showUploadList={false}
                beforeUpload={handleImageBeforeUpload}
                className="driver-upload-wrapper"
              >
                <div className="driver-upload-card">
                  <div className="driver-upload-avatar-wrapper">
                    {imageSrc || modalInfo?.image_url ? (
                      <img
                        src={imageSrc || modalInfo?.image_url}
                        alt="Driver"
                        className="driver-upload-avatar"
                      />
                    ) : (
                      <div className="driver-upload-avatar driver-upload-avatar--placeholder">
                        <UploadOutlined style={{ fontSize: 32 }} />
                      </div>
                    )}
                  </div>

                  <div className="driver-upload-text-main">
                    Click to Upload Driver Image
                  </div>
                  <div className="driver-upload-text-sub">Optional</div>
                </div>
              </Upload>
            </Form.Item>

            <button
              type="submit"
              className="btn loginBtn btn-sm btn-block w-25"
              disabled={loading}
            >
              {loading ? "Updating..." : "Update Driver"}
            </button>
          </Form>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default DriverModal;


















// import React, { useState, useEffect } from "react";
// import Modal from "react-bootstrap/Modal";
// import { Form, Input, Select, Upload } from "antd";
// import { UploadOutlined } from "@ant-design/icons";
// import "./index.css";
// import axios from "axios";
// import { switchEndpoint } from "../../../../../../redux/actions/endpoints";
// import { toast } from "react-toastify";

// const { Option } = Select;

// const DriverModal = ({ showDrop, handleCloseDrop, modalInfo, setModalInfo }) => {
//   const [loading, setLoading] = useState(false);
//   const [form] = Form.useForm();
//   const [imageUrl, setImageUrl] = useState(null);
//   const [driverImageFile, setDriverImageFile] = useState(null);

//   // const handleImageBeforeUpload = (file) => {
//   //   const previewUrl = URL.createObjectURL(file);
//   //   setImageUrl(previewUrl);
//   //   return false; // prevent AntD auto-upload
//   // };

//   const previewSrc = imageUrl || modalInfo?.image_url || null;

//   const handleImageBeforeUpload = (file) => {
//     // store the file for upload
//     setDriverImageFile(file);

//     // create a preview URL (no base64)
//     const previewUrl = URL.createObjectURL(file);
//     setImageUrl(previewUrl);

//     // prevent AntD from auto-uploading
//     return false;
//   };

//   const handleUploadDriversImage = async () => {
//     if (!driverImageFile) {
//       // no file selected
//       toast.error("Please select an image first");
//       return;
//     }

//     const formData = new FormData();
//     // adjust field name ("image") to what your backend expects
//     formData.append("file", driverImageFile);

//     setLoading(true);
//     const token = storage.getItem("token");
//     const toastConfig = {
//       position: "top-right",
//       autoClose: 5000,
//       hideProgressBar: false,
//       pauseOnHover: true,
//       progress: undefined,
//       theme: "light",
//     };

//     try {
//       const response = await axios.post(
//         `${switchEndpoint}/upload-image`,
//         formData,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             // ❗ DO NOT manually set "Content-Type": axios will set the correct multipart boundary
//           },
//         }
//       );

//       const { data } = response;
//       const uploadedUrl = data?.image_url;
//       // handleRegisterDriverWithImage(uploadedUrl);
//     } catch (error) {
//       console.error(error);
//       toast.error(
//         error?.response?.data?.message || "Failed to upload image",
//         toastConfig
//       );
//     } finally {
//       setLoading(false);
//     }
//   };

//   // const handleRegisterDriverWithImage = async (image) => {
//   //   let payload = {
//   //     name: driversName,
//   //     phone: driversPhone,
//   //     vehicle_number: plate,
//   //     vehicle_type: type,
//   //     image_url: image
//   //   }
//   //   setLoading(true);
//   //   const token = storage.getItem("token");
//   //   const toastConfig = {
//   //     position: "top-right",
//   //     autoClose: 5000,
//   //     hideProgressBar: false,
//   //     pauseOnHover: true,
//   //     progress: undefined,
//   //     theme: "light",
//   //   };

//   //   try {
//   //     const response = await axios.post(`${switchEndpoint}/factory/create-driver`, payload, {
//   //       headers: { Authorization: `Bearer ${token}` },
//   //     });
//   //     const { data } = response;
//   //     setLoading(false);
//   //     toast.success(data?.message || "Driver created successfully!", toastConfig);
//   //     handleCloseDrop();
//   //     window.location.reload();
//   //   } catch (error) {
//   //     toast.error(error?.response?.data?.message, toastConfig);
//   //   } finally {
//   //     setLoading(false);
//   //   }
//   // };

//   // const handleRegisterDriverWithoutImage = async () => {
//   //   let payload = {
//   //     name: driversName,
//   //     phone: driversPhone,
//   //     vehicle_number: plate,
//   //     vehicle_type: type,
//   //     image_url: ""
//   //   }
//   //   setLoading(true);
//   //   const token = storage.getItem("token");
//   //   const toastConfig = {
//   //     position: "top-right",
//   //     autoClose: 5000,
//   //     hideProgressBar: false,
//   //     pauseOnHover: true,
//   //     progress: undefined,
//   //     theme: "light",
//   //   };

//   //   try {
//   //     const response = await axios.post(`${switchEndpoint}/factory/create-driver`, payload, {
//   //       headers: { Authorization: `Bearer ${token}` },
//   //     });
//   //     const { data } = response;
//   //     setLoading(false);
//   //     toast.success(data?.message || "Driver created successfully!", toastConfig);
//   //     handleCloseDriver();
//   //     window.location.reload();
//   //   } catch (error) {
//   //     toast.error(error?.response?.data?.message, toastConfig);
//   //   } finally {
//   //     setLoading(false);
//   //   }
//   // };

//   useEffect(() => {
//     if (showDrop && modalInfo) {
//       form.setFieldsValue({
//         driverName: modalInfo.name || "",
//         driverPhone: modalInfo.phone || "",
//         vehiclePlate: modalInfo.vehicle_number || "",
//         vehicleType: modalInfo.vehicle_type || "",
//       });
//     } else if (!showDrop) {
//       form.resetFields();
//     }
//   }, [showDrop, modalInfo, form]);

//   const onFinish = async (values) => {
//     // console.log("submit values:", values);
//     // if (imageUrl) {
//     //   if (!driverImageFile) {
//     //     // no file selected
//     //     toast.error("Please select an image first");
//     //     return;
//     //   }

//     //   const formData = new FormData();
//     //   // adjust field name ("image") to what your backend expects
//     //   formData.append("file", driverImageFile);

//     //   setLoading(true);
//     //   const token = storage.getItem("token");
//     //   const toastConfig = {
//     //     position: "top-right",
//     //     autoClose: 5000,
//     //     hideProgressBar: false,
//     //     pauseOnHover: true,
//     //     progress: undefined,
//     //     theme: "light",
//     //   };

//     //   try {
//     //     const response = await axios.post(
//     //       `${switchEndpoint}/upload-image`,
//     //       formData,
//     //       {
//     //         headers: {
//     //           Authorization: `Bearer ${token}`,
//     //           // ❗ DO NOT manually set "Content-Type": axios will set the correct multipart boundary
//     //         },
//     //       }
//     //     );

//     //     const { data } = response;
//     //     const uploadedUrl = data?.image_url;
//     //     let payload = {
//     //       name: values.driverName,
//     //       phone: values.driverPhone,
//     //       vehicle_number: values.vehiclePlate,
//     //       vehicle_type: values.vehicleType,
//     //       image_url: uploadedUrl
//     //     }
//     //     setLoading(true);
//     //       const token = storage.getItem("token");
//     //       const toastConfig = {
//     //         position: "top-right",
//     //         autoClose: 5000,
//     //         hideProgressBar: false,
//     //         pauseOnHover: true,
//     //         progress: undefined,
//     //         theme: "light",
//     //       };
      
//     //       try {
//     //         const response = await axios.patch(`${switchEndpoint}/factory/update-driver/${modalInfo?.id}`, payload, {
//     //           headers: { Authorization: `Bearer ${token}` },
//     //         });
//     //         const { data } = response;
//     //         setLoading(false);
//     //         toast.success(data?.message || "Driver created successfully!", toastConfig);
//     //         handleCloseDrop();
//     //         window.location.reload();
//     //       } catch (error) {
//     //         toast.error(error?.response?.data?.message, toastConfig);
//     //       } finally {
//     //         setLoading(false);
//     //       }
//     //   } catch (error) {
//     //     console.error(error);
//     //     toast.error(
//     //       error?.response?.data?.message || "Failed to upload image",
//     //       toastConfig
//     //     );
//     //   } finally {
//     //     setLoading(false);
//     //   }
//     // }
//     let payload = {
//       name: values.driverName,
//       phone: values.driverPhone,
//       vehicle_number: values.vehiclePlate,
//       vehicle_type: values.vehicleType,
//       image_url: modalInfo?.image_url
//     }
//     setLoading(true);
//     const token = storage.getItem("token");
//     const toastConfig = {
//       position: "top-right",
//       autoClose: 5000,
//       hideProgressBar: false,
//       pauseOnHover: true,
//       progress: undefined,
//       theme: "light",
//     };

//     try {
//       const response = await axios.patch(`${switchEndpoint}/factory/update-driver/${modalInfo?.id}`, payload, {
//         headers: { Authorization: `Bearer ${token}` },
//       });
//       const { data } = response;
//       setLoading(false);
//       toast.success(data?.message || "Driver updated successfully!", toastConfig);
//       console.log("")
//       handleCloseDrop();
//     } catch (error) {
//       toast.error(error?.response?.data?.message, toastConfig);
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <Modal
//       show={showDrop}
//       onHide={() => {
//         handleCloseDrop();
//         setModalInfo({});
//       }}
//       backdrop="static"
//       keyboard={false}
//       size="lg"
//     >
//       <Modal.Header closeButton />
//       <Modal.Body>
//         <div className="mx-2">
//           <Form
//             layout="vertical"
//             form={form}
//             onFinish={onFinish}
//             className="driver-form"
//           >
//             <div className="driver-form-grid">
//               <Form.Item
//                 label="Driver Name"
//                 name="driverName"
//                 rules={[
//                   { required: true, message: "Please enter driver name" },
//                 ]}
//               >
//                 <Input
//                   className="inputStyle"
//                   placeholder="Enter driver name"
//                 />
//               </Form.Item>

//               <Form.Item
//                 label="Driver Phone Number"
//                 name="driverPhone"
//                 rules={[
//                   {
//                     required: true,
//                     message: "Please enter driver phone number",
//                   },
//                 ]}
//               >
//                 <Input
//                   type="number"
//                   className="inputStyle"
//                   placeholder="Enter driver phone number"
//                 />
//               </Form.Item>
//             </div>

//             <div className="driver-form-grid">
//               <Form.Item
//                 label="Vehicle Plate No"
//                 name="vehiclePlate"
//                 rules={[
//                   { required: true, message: "Please enter vehicle plate no" },
//                 ]}
//               >
//                 <Input
//                   className="inputStyle"
//                   placeholder="Enter vehicle plate no"
//                 />
//               </Form.Item>

//               <Form.Item
//                 label="Vehicle Type"
//                 name="vehicleType"
//                 rules={[
//                   { required: true, message: "Please select vehicle type" },
//                 ]}
//               >
//                 <Select placeholder="Select vehicle type">
//                   <Option value="tricycle">Tricycle</Option>
//                   <Option value="car">Car</Option>
//                   <Option value="bike">Bike</Option>
//                 </Select>
//               </Form.Item>
//             </div>

//             <Form.Item label="">
//               <Upload
//                 accept="image/*"
//                 showUploadList={false}
//                 beforeUpload={handleImageBeforeUpload}
//                 className="driver-upload-wrapper"
//               >
//                 <div className="driver-upload-card">
//                   <div className="driver-upload-avatar-wrapper">
//                     {previewSrc ? (
//                       <img
//                         src={previewSrc}
//                         alt="Driver"
//                         className="driver-upload-avatar"
//                       />
//                     ) : (
//                       <div className="driver-upload-avatar driver-upload-avatar--placeholder">
//                         <UploadOutlined style={{ fontSize: 32 }} />
//                       </div>
//                     )}
//                   </div>

//                   <div className="driver-upload-text-main">
//                     Click to Upload Driver Image
//                   </div>
//                   <div className="driver-upload-text-sub">Optional</div>
//                 </div>
//               </Upload>
//             </Form.Item>

//             <button
//               type="submit"
//               className="btn loginBtn btn-sm btn-block w-25"
//               disabled={loading}
//             >
//               Update Driver
//             </button>
//           </Form>
//         </div>
//       </Modal.Body>
//     </Modal>
//   );
// };

// export default DriverModal;
